import React from "react";
import { motion } from "framer-motion";

export function PricingCheckIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      className="mt-0.5 h-4 w-4 shrink-0"
      aria-hidden="true"
    >
      <circle cx="10" cy="10" r="10" className="fill-primary" />
      <path
        d="M5.4 10.2 8.3 13l6-6"
        fill="none"
        stroke="black"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Plan card chrome matching the landing "Pick a plan" section (PricingSection.jsx).
 * Use either `href` (link CTA) or `onCta` (button CTA for checkout).
 */
export default function PricingPlanCardLanding({
  name,
  subtitle = "",
  price,
  priceSuffix = "per Month",
  features = [],
  cta,
  elevated = false,
  href,
  onCta,
  ctaDisabled = false,
  ctaLoading = false,
  motionProps = {},
}) {
  const ctaClass =
    "font-secondary mt-6 inline-flex w-full min-h-[48px] items-center justify-center whitespace-nowrap rounded-sm bg-primary px-6 py-3 text-[14px] font-semibold leading-tight text-black transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50";

  return (
    <motion.article
      {...motionProps}
      className={`flex flex-col rounded-sm bg-[#242424] ${
        elevated
          ? "relative z-20 bg-[#2A2A2A] h-[105%] border-t-8 border-primary brightness-105 sm:p-8 lg:scale-[1.19] lg:px-7 lg:py-11"
          : "h-full p-6 sm:p-7"
      }`}
    >
      <span className="font-secondary inline-flex w-fit rounded-sm bg-primary/15 px-3 py-2 text-xs uppercase tracking-[0.08em] text-primary">
        {name}
      </span>

      <p className="font-secondary mt-3 min-h-[8rem] text-[13px] leading-snug text-gray-300">
        {subtitle || "\u00a0"}
      </p>

      <div className="mt-4">
        <p className="font-title text-5xl font-semibold leading-none tracking-tight text-white sm:text-6xl">
          {price}
        </p>
        <p className="font-secondary mt-1.5 text-md text-white">
          {priceSuffix}
        </p>
      </div>

      <div className="mt-10 h-px w-full bg-white/10" />

      <ul className="mt-4 flex flex-1 flex-col gap-6">
        {features.map((feature) => (
          <li
            key={`${name}-${feature}`}
            className="font-secondary flex items-start gap-2.5 text-sm leading-[1.4] text-white"
          >
            <PricingCheckIcon />
            <span className="font-secondary">{feature}</span>
          </li>
        ))}
      </ul>

      {onCta ? (
        <motion.button
          type="button"
          onClick={onCta}
          disabled={ctaDisabled || ctaLoading}
          className={ctaClass}
          whileHover={ctaDisabled || ctaLoading ? {} : { scale: 1.02 }}
          whileTap={ctaDisabled || ctaLoading ? {} : { scale: 0.98 }}
        >
          {ctaLoading ? (
            <span className="flex items-center justify-center gap-2">
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-black border-t-transparent" />
              Processing…
            </span>
          ) : (
            cta
          )}
        </motion.button>
      ) : (
        <a href={href || "/#/signup"} className={ctaClass}>
          {cta}
        </a>
      )}
    </motion.article>
  );
}
